/**
 * Frontend API-Client fuer Schwellen-Verwaltung (Phase 12).
 *
 * Backend-Routes (siehe Plan 12-03):
 *   GET  /programmes/auftragsverfolgung/thresholds
 *   PUT  /programmes/auftragsverfolgung/thresholds/:key
 *
 * WICHTIG: KEIN /api/-Prefix — api-Client hat baseURL /api bereits gesetzt
 * (siehe core/frontend/src/services/api.ts).
 */
import { api } from '@libero/core/services/api';

export interface ThresholdRow {
  key: string;
  category: 'dhl' | 'supplier';
  label: string;
  thresholdHours: number;
  thresholdWarnHours: number | null;
  alertEmailEnabled: boolean;
  isActive: boolean;
}

export interface UpdateThresholdPayload {
  threshold_hours: number;
  threshold_warn_hours?: number | null;
  alert_email_enabled?: boolean;
  is_active?: boolean;
}

/**
 * Laedt alle Schwellen aus dem Backend.
 * Gibt ein leeres Array zurueck wenn thresholds-Feld fehlt.
 */
export async function getThresholds(): Promise<ThresholdRow[]> {
  try {
    const res = await api.get('/programmes/auftragsverfolgung/thresholds');
    return (res.data?.data?.thresholds as ThresholdRow[]) ?? [];
  } catch (err: unknown) {
    const e = err as {
      response?: { data?: { error?: { message?: string } } };
      message?: string;
    };
    const msg =
      e?.response?.data?.error?.message ??
      e?.message ??
      'Schwellen konnten nicht geladen werden';
    throw new Error(msg);
  }
}

/**
 * Aktualisiert eine Schwelle via PUT.
 * Der key wird URI-enkodiert (schutzt gegen Sonderzeichen in kuenftigen Keys).
 */
export async function updateThreshold(
  key: string,
  payload: UpdateThresholdPayload,
): Promise<ThresholdRow> {
  try {
    const res = await api.put(
      `/programmes/auftragsverfolgung/thresholds/${encodeURIComponent(key)}`,
      payload,
    );
    return res.data?.data?.threshold as ThresholdRow;
  } catch (err: unknown) {
    const e = err as {
      response?: { data?: { error?: { message?: string } } };
      message?: string;
    };
    const msg =
      e?.response?.data?.error?.message ??
      e?.message ??
      'Update fehlgeschlagen';
    throw new Error(msg);
  }
}
